"use client";

import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import ButtonArrow from "@/components/ButtonArrow";
import "@/styles/chunks/solution.css";
import { API_ENDPOINTS } from "@/config/api";
import { useResolvedCountry } from "@/providers/ResolvedCountryContext";
import { axiosConnector } from "@/redux/services/axiosConnector";
import {
  detectUserCountry,
  FALLBACK_COUNTRY,
} from "@/util/detectUserCountry";
import { getRecaptchaToken, RECAPTCHA_ACTIONS } from "@/util/recaptcha";
import { withCountryHref } from "@/util/seoCountryPaths";
import { storeThankYouSubmission } from "@/util/thankYouSubmission";
import { getLeadAttributionPayload } from "@/util/leadAttribution";
import { QUOTE_PACKAGE_SELECT_EVENT } from "@/components/PackageCard/PackageCard";

type FormStatus = {
  type: "idle" | "success" | "error";
  message: string;
};

type QuoteFormValues = {
  name: string;
  phone: string;
  email: string;
  package: string;
  extraFields: Record<string, string>;
};

export type QuoteSourceDetails = {
  id?: string;
  name?: string;
  slug?: string;
};

export type QuotePackageOption = {
  name: string;
  price?: string;
};

export type QuoteExtraFormField = {
  label?: string;
  type?: string;
  inputType?: "text" | "textarea" | "dropdown";
  options?: string[];
  placeholder?: string;
  required?: boolean;
};

type QuoteSource = "solution" | "service";

type GetQuoteFormProps = {
  source?: QuoteSource;
  solutionDetails?: QuoteSourceDetails;
  serviceDetails?: QuoteSourceDetails;
  countryTimeZone?: string | null;
  packages?: QuotePackageOption[];
  extraFormFields?: QuoteExtraFormField[];
  submitLabel?: string;
};

const DEFAULT_SUBMIT_LABEL = "Get a quote in 24 hours";

const initialStatus: FormStatus = { type: "idle", message: "" };

type QuotePackageSelectSyncProps = {
  packageNames: string[];
  setFieldValue: (field: string, value: string) => void;
};

const QuotePackageSelectSync = ({
  packageNames,
  setFieldValue,
}: QuotePackageSelectSyncProps) => {
  useEffect(() => {
    const onPackageSelect = (event: Event) => {
      const name = String(
        (event as CustomEvent<{ name?: string }>).detail?.name ?? ""
      ).trim();
      if (!name || !packageNames.includes(name)) return;
      setFieldValue("package", name);
    };

    window.addEventListener(QUOTE_PACKAGE_SELECT_EVENT, onPackageSelect);
    return () => {
      window.removeEventListener(QUOTE_PACKAGE_SELECT_EVENT, onPackageSelect);
    };
  }, [packageNames, setFieldValue]);

  return null;
};

const GetQuoteForm = ({
  source = "service",
  solutionDetails,
  serviceDetails,
  countryTimeZone,
  packages = [],
  extraFormFields = [],
  submitLabel = DEFAULT_SUBMIT_LABEL,
}: GetQuoteFormProps) => {
  const router = useRouter();
  const country = useResolvedCountry();
  const [status, setStatus] = useState<FormStatus>(initialStatus);
  const [defaultCountry, setDefaultCountry] = useState(FALLBACK_COUNTRY);
  const [detectedCountryName, setDetectedCountryName] = useState("");
  // const timezoneLabel = countryTimeZone?.trim() || "";

  const packageOptions = useMemo(
    () =>
      packages.filter(
        (pkg): pkg is QuotePackageOption => Boolean(pkg?.name?.trim())
      ),
    [packages]
  );
  const packageNames = useMemo(
    () => packageOptions.map((pkg) => pkg.name),
    [packageOptions]
  );
  const hasPackages = packageOptions.length > 0;

  const resolvedExtraFields = useMemo(
    () =>
      (extraFormFields ?? [])
        .map((field, index) => {
          const label = field.label?.trim() || "";
          const typeFromCms = field.type?.trim() || "";
          const type =
            typeFromCms ||
            label
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "_")
              .replace(/^_+|_+$/g, "") ||
            `field_${index + 1}`;
          return {
            label,
            type,
            inputType: field.inputType || "text",
            options: (field.options ?? [])
              .map((opt) => String(opt ?? "").trim())
              .filter(Boolean),
            placeholder: field.placeholder?.trim() || "",
            required: Boolean(field.required),
          };
        })
        .filter((field) => field.label),
    [extraFormFields]
  );

  const initialValues = useMemo<QuoteFormValues>(
    () => ({
      name: "",
      phone: "",
      email: "",
      package: "",
      extraFields: Object.fromEntries(
        resolvedExtraFields.map((field) => [field.type, ""])
      ),
    }),
    [resolvedExtraFields]
  );

  const validationSchema = useMemo(() => {
    const extraShape: Record<string, Yup.StringSchema> = {};
    for (const field of resolvedExtraFields) {
      extraShape[field.type] = field.required
        ? Yup.string()
            .trim()
            .required(`${field.label} is required`)
        : Yup.string().trim();
    }

    return Yup.object({
      name: Yup.string().trim().required("Name is required"),
      phone: Yup.string()
        .trim()
        .required("Phone is required")
        .min(8, "Please enter a valid phone number"),
      email: Yup.string()
        .trim()
        .email("Please enter a valid email address")
        .required("Email is required"),
      package: hasPackages
        ? Yup.string().trim().required("Please select a package")
        : Yup.string().trim(),
      extraFields: Yup.object().shape(extraShape),
    });
  }, [hasPackages, resolvedExtraFields]);

  useEffect(() => {
    let cancelled = false;

    detectUserCountry().then(({ code, name }) => {
      if (cancelled) return;
      setDefaultCountry(code);
      setDetectedCountryName(name);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (
    values: QuoteFormValues,
    { resetForm }: { resetForm: () => void }
  ) => {
    setStatus(initialStatus);

    const selectedPackage = values.package.trim();
    const selectedPackageOption = packageOptions.find(
      (pkg) => pkg.name === selectedPackage
    );
    const selectedPackageAmount = selectedPackageOption?.price?.trim() || "";
    const extraFormFieldAnswers = resolvedExtraFields
      .map((field) => ({
        type: field.type,
        label: field.label,
        value: String(values.extraFields?.[field.type] ?? "").trim(),
      }))
      .filter((item) => item.value);

    const details =
      source === "service"
        ? {
            id: serviceDetails?.id ?? "",
            name: serviceDetails?.name ?? "",
            slug: serviceDetails?.slug ?? "",
            ...(selectedPackage
              ? {
                  packageDetails: {
                    packageName: selectedPackage,
                    packageAmount: selectedPackageAmount,
                  },
                }
              : {}),
            ...(extraFormFieldAnswers.length
              ? { extraFormFieldAnswers }
              : {}),
          }
        : {
            id: solutionDetails?.id ?? "",
            name: solutionDetails?.name ?? "",
            slug: solutionDetails?.slug ?? "",
          };

    try {
      const attribution = getLeadAttributionPayload();
      const recaptchaToken = await getRecaptchaToken(RECAPTCHA_ACTIONS.getQuote);
      const res = await axiosConnector(
        "post",
        API_ENDPOINTS.contactLeads,
        {
          name: values.name.trim(),
          phone: values.phone.trim(),
          email: values.email.trim(),
          country: detectedCountryName || defaultCountry.toUpperCase(),
          source,
          recaptchaToken,
          recaptchaAction: RECAPTCHA_ACTIONS.getQuote,
          ...(attribution ? { attribution } : {}),
          ...(source === "service"
            ? { serviceDetails: details }
            : { solutionDetails: details }),
        },
        undefined,
        undefined,
        { throwOnError: true, maxAttempts: 1 }
      );

      if (!res?.success) {
        setStatus({
          type: "error",
          message: res?.message || "Unable to submit enquiry. Please try again.",
        });
        return;
      }

      storeThankYouSubmission({
        formType: "get-quote",
        name: values.name.trim(),
        email: values.email.trim(),
        contextLabel: details.name?.trim() || undefined,
        packageName: selectedPackage || undefined,
        packageAmount: selectedPackageAmount || undefined,
        extraFormFieldAnswers: extraFormFieldAnswers.length
          ? extraFormFieldAnswers
          : undefined,
        summary: "Thanks — our team will reply within 24 hours.",
      });
      resetForm();
      router.push(withCountryHref("/thank-you", country));
    } catch (error: unknown) {
      const apiMessage =
        typeof error === "object" &&
        error !== null &&
        "response" in error &&
        typeof (error as { response?: { data?: { message?: string } } }).response
          ?.data?.message === "string"
          ? (error as { response: { data: { message: string } } }).response.data
              .message
          : null;

      setStatus({
        type: "error",
        message: apiMessage || "Unable to submit enquiry. Please try again.",
      });
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, values, setFieldValue, setFieldTouched }) => (
        <Form className="loc-form" noValidate>
          <QuotePackageSelectSync
            packageNames={packageNames}
            setFieldValue={setFieldValue}
          />
          <div className="loc-field">
            <label htmlFor="loc-name" className="visually-hidden">
              Name
            </label>
            <Field
              type="text"
              id="loc-name"
              name="name"
              placeholder="Your name *"
              autoComplete="name"
            />
            <ErrorMessage name="name" component="p" className="loc-form-status is-error" />
          </div>
          <div className="loc-field">
            <label htmlFor="loc-phone" className="visually-hidden">
              Phone
            </label>
            <PhoneInput
              key={defaultCountry}
              country={defaultCountry}
              value={values.phone}
              onChange={(value) => {
                setFieldValue("phone", value);
              }}
              onBlur={() => setFieldTouched("phone", true)}
              inputProps={{
                id: "loc-phone",
                name: "phone",
                autoComplete: "tel",
                placeholder: "Phone *",
              }}
            //   enableSearch
              preferredCountries={["au", "in", "us", "gb"]}
              inputClass="loc-phone-input"
              containerClass="loc-phone-container"
              buttonClass="loc-phone-button"
              dropdownClass="loc-phone-dropdown"
              searchClass="loc-phone-search"
            />
            <ErrorMessage name="phone" component="p" className="loc-form-status is-error" />
          </div>
          <div className="loc-field">
            <label htmlFor="loc-email" className="visually-hidden">
              Email
            </label>
            <Field
              type="email"
              id="loc-email"
              name="email"
              placeholder="Your email address *"
              autoComplete="email"
            />
            <ErrorMessage name="email" component="p" className="loc-form-status is-error" />
          </div>
          {hasPackages ? (
            <div className="loc-field">
              <label htmlFor="loc-package" className="visually-hidden">
                Package
              </label>
              <Field as="select" id="loc-package" name="package">
                <option value="">Select a package *</option>
                {packageOptions.map((pkg) => (
                  <option key={pkg.name} value={pkg.name}>
                    {pkg.price?.trim()
                      ? `${pkg.name} — ${pkg.price.trim()}`
                      : pkg.name}
                  </option>
                ))}
              </Field>
              <ErrorMessage
                name="package"
                component="p"
                className="loc-form-status is-error"
              />
            </div>
          ) : null}
          {resolvedExtraFields.map((field) => {
            const fieldId = `loc-extra-${field.type}`;
            const fieldName = `extraFields.${field.type}`;
            const placeholder = field.required
              ? `${field.placeholder || field.label} *`
              : field.placeholder || field.label;

            return (
              <div className="loc-field" key={field.type}>
                <label htmlFor={fieldId} className="visually-hidden">
                  {field.label}
                </label>
                {field.inputType === "dropdown" ? (
                  <Field as="select" id={fieldId} name={fieldName}>
                    <option value="">{placeholder || `Select ${field.label}`}</option>
                    {field.options.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Field>
                ) : field.inputType === "textarea" ? (
                  <Field
                    as="textarea"
                    id={fieldId}
                    name={fieldName}
                    rows={3}
                    placeholder={placeholder}
                  />
                ) : (
                  <Field
                    type={field.inputType}
                    id={fieldId}
                    name={fieldName}
                    placeholder={placeholder}
                  />
                )}
                <ErrorMessage
                  name={fieldName}
                  component="p"
                  className="loc-form-status is-error"
                />
              </div>
            );
          })}
          <button
            type="submit"
            className="btn btn-primary loc-form-submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : submitLabel} <ButtonArrow />
          </button>
          {status.type === "error" ? (
            <p
              className="loc-form-status is-error"
              id="locHeroFormStatus"
              role="status"
              aria-live="polite"
            >
              {status.message}
            </p>
          ) : null}
        </Form>
      )}
    </Formik>
  );
};

export default GetQuoteForm;
